import { Component, OnInit } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { SelectPesquisavelComponent } from '../../../../shared/components/select-pesquisavel/select-pesquisavel.component';

import { CategoriaFinanceiraService } from '../../../../shared/services/apis/financas/categoria-financeira.service';
import { CategoriaFinanceiraRequest } from '../../../../shared/services/requests/financas/categoria-financeira.request';
import { TipoCategoriaFinanceira } from '../../../../shared/services/models/financas/tipo-categoria-financeira.model';
import { TipoCategoriaDre } from '../../../../shared/services/models/financas/tipo-categoria-dre.model';
import { CategoriaFinanceira } from '../../../../shared/services/models/financas/categoria-financeira.model';
import { MigracaoCategoriaModalComponent } from '../config-financas-shared/migracao-categoria-modal/migracao-categoria-modal.component';

// Removido a interface de optgroup

@Component({
  selector: 'app-categoria-financeira-cadastro',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSlideToggleModule,
    LoadingBoxComponent,
    FooterActionComponent,
    HeaderComponent,
    SelectPesquisavelComponent,
  ],
  templateUrl: './categoria-financeira-cadastro.component.html',
  styleUrl: './categoria-financeira-cadastro.component.css',
})
export class CategoriaFinanceiraCadastroComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Financas;

  public form: FormGroup;
  public isLoading: boolean = false;
  public isLoadingLookups: boolean = false;
  public isSaving: boolean = false;
  public isEdicao: boolean = false;
  public idCategoria: number = 0;

  // Lookups data
  public tiposFinancas: TipoCategoriaFinanceira[] = [];
  public categoriasPai: CategoriaFinanceira[] = [];
  public categoriasPaiValidas: CategoriaFinanceira[] = [];
  // Configuração Hierárquica Plana do DRE para Select Pesquisável
  public dreFlatList: any[] = [];

  private profundidadeSubarvoreAtual = 0;

  constructor(
    private formBuilder: FormBuilder,
    private categoriaService: CategoriaFinanceiraService,
    private alertService: AlertService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.createForm();
    this.carregarLookups();
    this.configurarListenerDre();
    this.configurarListenerTipo();
  }

  private configurarListenerTipo(): void {
    this.form.get('idTipoCategoriaFinanceira')?.valueChanges.subscribe(() => this.filtrarCategoriasPaiValidas());
  }

  /**
   * Filtra as categorias pai candidatas: exclui a própria categoria (em edição), as que
   * estourariam o limite de 3 níveis (considerando a subárvore da categoria editada) e as
   * de tipo (Receita/Despesa) diferente do selecionado no formulário.
   */
  private filtrarCategoriasPaiValidas(): void {
    const tipoSelecionado = this.form?.get('idTipoCategoriaFinanceira')?.value;

    this.categoriasPaiValidas = this.categoriasPai.filter(cat => {
      if (this.isEdicao && cat.idCategoriaFinanceira === this.idCategoria) return false;

      // categoriasPai já vem limitada pelo backend a níveis 1 e 2 (sem netos)
      const nivelCandidato = cat.idCategoriaFinanceiraPai == null ? 1 : 2;
      if (nivelCandidato + 1 + this.profundidadeSubarvoreAtual > 3) return false;

      if (tipoSelecionado && cat.idTipoCategoriaFinanceira !== tipoSelecionado) return false;

      return true;
    });
  }

  private configurarListenerDre(): void {
    this.form.get('idTipoCategoriaDre')?.valueChanges.subscribe(idDre => {
      if (!idDre) return;

      // Busca o DRE inteiro na nossa lista cacheada
      const dreSelecionado = this.dreFlatList.find(d => d.idTipoCategoriaDre === idDre);
      if (dreSelecionado) {
        // Checa se é 'Não Considera' (texto base ou ID default)
        // Usando trim e lower case para garantir segurança na comparação
        const isNaoConsidera =
          dreSelecionado.descricao.toLowerCase().includes('considera') ||
          !dreSelecionado.idTipoCategoriaFinanceira;

        const controlTipo = this.form.get('idTipoCategoriaFinanceira');

        if (isNaoConsidera) {
          controlTipo?.enable();
        } else {
          // Preenche automaticamente o Tipo vinculado a este DRE e bloqueia
          controlTipo?.setValue(dreSelecionado.idTipoCategoriaFinanceira);
          controlTipo?.disable();
        }
      }
    });
  }

  private createForm(): void {
    this.form = this.formBuilder.group({
      descricao: ['', [Validators.required, Validators.maxLength(100)]],
      idTipoCategoriaFinanceira: [null, Validators.required],
      idTipoCategoriaDre: [null, Validators.required],
      idCategoriaPai: [null],
      isAtivo: [true],
    });
  }

  private carregarLookups(): void {
    this.isLoadingLookups = true;
    this.categoriaService.getLookups().subscribe({
      next: res => {
        this.isLoadingLookups = false;
        if (!res.isError) {
          this.tiposFinancas = res.tiposCategoriaFinanceira || [];
          this.categoriasPai = res.categoriasPai || [];
          this.buildDreList(res.tiposCategoriaDre || []);
          this.filtrarCategoriasPaiValidas();

          this.checkEdicao(); // Verifica a edição após popular os lookups
        } else {
          this.alertService.show(
            'Erro',
            'Não foi possível carregar os dados auxiliares do formulário.',
            AlertType.error
          );
        }
      },
      error: err => {
        this.isLoadingLookups = false;
        this.alertService.show('Erro', 'Ocorreu um erro ao carregar os dados auxiliares.', AlertType.error);
      },
    });
  }

  private buildDreList(flatDres: TipoCategoriaDre[]): void {
    this.dreFlatList = [];

    const buildTree = (parentId: number | null | undefined, level: number) => {
      const children = flatDres.filter(d =>
        parentId ? d.idTipoCategoriaDrePai === parentId : !d.idTipoCategoriaDrePai
      );

      children.forEach(node => {
        const hasChildren = flatDres.some(d => d.idTipoCategoriaDrePai === node.idTipoCategoriaDre);
        // Indentação baseada no Nível da Árvore
        const indent = 'ㅤㅤ'.repeat(level);

        this.dreFlatList.push({
          ...node,
          descricao: indent + node.descricao,
          // Regra: Bloqueia se possui filhos (ex: grandes Grupos DRE), libera se é nó folha (Neto ou Isolado)
          disabled: hasChildren,
        });

        if (hasChildren) {
          buildTree(node.idTipoCategoriaDre, level + 1);
        }
      });
    };

    buildTree(null, 0);
  }

  private checkEdicao(): void {
    this.route.params.subscribe(params => {
      if (params['idCategoriaFinanceira']) {
        this.idCategoria = +params['idCategoriaFinanceira'];
        this.isEdicao = true;
        this.carregarCategoria();
      }
    });
  }

  private carregarCategoria(): void {
    this.isLoading = true;
    this.categoriaService.getById(this.idCategoria).subscribe({
      next: res => {
        this.isLoading = false;
        if (res.isError) {
          this.alertService.show(
            'Erro',
            res.errorDescription || 'Erro ao carregar a categoria.',
            AlertType.error
          );
          this.voltar();
          return;
        }
        if (res.categoria) {
          this.profundidadeSubarvoreAtual = res.categoria.profundidadeSubarvore || 0;
          this.filtrarCategoriasPaiValidas();

          this.form.patchValue({
            descricao: res.categoria.categoriaFinanceira.descricao,
            idTipoCategoriaFinanceira: res.categoria.categoriaFinanceira.idTipoCategoriaFinanceira,
            idTipoCategoriaDre: res.categoria.categoriaFinanceira.idTipoCategoriaDre,
            idCategoriaPai: res.categoria.categoriaFinanceira.idCategoriaFinanceiraPai,
            isAtivo: res.categoria.categoriaFinanceira.isAtivo,
          });
        }
      },
      error: err => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Ocorreu um erro ao carregar os dados da categoria.', AlertType.error);
        this.voltar();
      },
    });
  }

  public salvar(idCategoriaMigracao?: number): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alertService.show('Atenção', 'Preencha todos os campos obrigatórios.', AlertType.warning);
      return;
    }

    this.isSaving = true;
    // getRawValue garante a captura do idTipoCategoriaFinanceira, mesmo se o control estiver disable()
    const formValue = this.form.getRawValue();

    const request = new CategoriaFinanceiraRequest();
    request.descricao = formValue.descricao;
    request.idTipoCategoriaFinanceira = formValue.idTipoCategoriaFinanceira;
    request.idTipoCategoriaDre = formValue.idTipoCategoriaDre;
    request.idCategoriaFinanceiraPai = formValue.idCategoriaPai;
    request.isAtivo = formValue.isAtivo;
    if (idCategoriaMigracao) {
      request.idCategoriaMigracao = idCategoriaMigracao;
    }

    if (this.isEdicao) {
      this.categoriaService.update(this.idCategoria, request).subscribe({
        next: res => this.tratarRetorno(res, request),
        error: err => this.tratarErro(err),
      });
    } else {
      this.categoriaService.create(request).subscribe({
        next: res => this.tratarRetorno(res, request),
        error: err => this.tratarErro(err),
      });
    }
  }

  private tratarRetorno(res: any, request: CategoriaFinanceiraRequest): void {
    this.isSaving = false;
    if (res.isError) {
      if (res.errorCode === -999 && request.idCategoriaFinanceiraPai) {
        const dialogRef = this.dialog.open(MigracaoCategoriaModalComponent, {
          width: '500px',
          data: {
            idCategoriaOrigem: request.idCategoriaFinanceiraPai,
            idTipoCategoriaFinanceira: request.idTipoCategoriaFinanceira,
            mensagemExibicao: res.errorDescription,
          },
        });

        dialogRef.afterClosed().subscribe((idDestino: number | undefined) => {
          if (idDestino) {
            // Refaz a submissão enviando o parâmetro final
            this.salvar(idDestino);
          }
        });
        return;
      }
      this.alertService.show(
        'Atenção',
        res.errorDescription || 'Erro ao salvar a categoria.',
        AlertType.warning
      );
    } else {
      this.alertService.show('Sucesso', 'Categoria salva com sucesso.', AlertType.success);
      this.voltar();
    }
  }

  private tratarErro(err: any): void {
    this.isSaving = false;
    this.alertService.show('Erro', 'Ocorreu um erro inesperado ao tentar salvar.', AlertType.error);
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/financas/categorias']);
  }
}
